import { hasPermission } from '@/hooks/usePermission'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth.store'
import { Link, Outlet, createRoute, useLocation } from '@tanstack/react-router'
import {
  Banknote,
  BookOpen,
  Bot,
  Building2,
  Calculator,
  CreditCard,
  FileText,
  GraduationCap,
  Landmark,
  Layers,
  Lock,
  type LucideIcon,
  Mail,
  Palette,
  Settings2,
  UserCircle,
  UserCog,
  Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Route as appLayoutRoute } from '../layout'

interface SettingsNavItem {
  to: string
  labelKey: string
  group: 'account' | 'organization'
  icon: LucideIcon
  permission?: string
}

const ITEMS: SettingsNavItem[] = [
  { to: '/settings/profile', labelKey: 'nav.profile', group: 'account', icon: UserCircle },
  { to: '/settings/security', labelKey: 'nav.security', group: 'account', icon: Lock },
  { to: '/settings/preferences', labelKey: 'nav.preferences', group: 'account', icon: Settings2 },
  {
    to: '/settings/organization',
    labelKey: 'nav.organization',
    group: 'organization',
    icon: Building2,
    permission: 'org:read',
  },
  {
    // L6 — mon abonnement (accessible à tout utilisateur : la lecture
    // de sa propre facturation n'est jamais gatée par permission).
    to: '/settings/subscription',
    labelKey: 'nav.subscription',
    group: 'organization',
    icon: CreditCard,
  },
  {
    to: '/settings/users',
    labelKey: 'nav.users',
    group: 'organization',
    icon: Users,
    permission: 'users:read',
  },
  {
    to: '/settings/tariffs',
    labelKey: 'nav.tariffs',
    group: 'organization',
    icon: Layers,
    permission: 'tariffs:manage',
  },
  {
    to: '/settings/documents',
    labelKey: 'nav.documents',
    group: 'organization',
    icon: FileText,
    permission: 'org:update',
  },
  {
    to: '/settings/email',
    labelKey: 'nav.email',
    group: 'organization',
    icon: Mail,
    permission: 'org:update',
  },
  {
    to: '/settings/customs-referential',
    labelKey: 'nav.customs_referential',
    group: 'organization',
    icon: Landmark,
    permission: 'hs_codes:read',
  },
  {
    to: '/settings/copilot',
    labelKey: 'nav.copilot',
    group: 'organization',
    icon: Bot,
    permission: 'copilot:settings',
  },
  {
    to: '/settings/chart-of-accounts',
    labelKey: 'nav.chart_of_accounts',
    group: 'organization',
    icon: BookOpen,
    permission: 'chart_of_accounts:manage',
  },
  {
    to: '/settings/accounting',
    labelKey: 'nav.accounting',
    group: 'organization',
    icon: Calculator,
    permission: 'chart_of_accounts:manage',
  },
  {
    to: '/settings/payroll',
    labelKey: 'nav.payroll_parameters',
    group: 'organization',
    icon: Banknote,
    permission: 'payroll:parameters',
  },
  {
    to: '/settings/hr',
    labelKey: 'nav.hr_catalog',
    group: 'organization',
    icon: UserCog,
    permission: 'hr_catalog:manage',
  },
  {
    to: '/settings/branding',
    labelKey: 'nav.branding',
    group: 'organization',
    icon: Palette,
    permission: 'org:update',
  },
  {
    to: '/settings/learn',
    labelKey: 'nav.learn',
    group: 'organization',
    icon: GraduationCap,
    permission: 'learn:write',
  },
]

function useVisibleItems(): SettingsNavItem[] {
  const perms = useAuthStore((s) => s.permissions)
  return ITEMS.filter((item) => !item.permission || hasPermission(perms, item.permission))
}

function SettingsMobileTabs() {
  const { t } = useTranslation('settings')
  const location = useLocation()
  const items = useVisibleItems()

  return (
    <div className="-mx-4 overflow-x-auto pb-1 md:hidden">
      <div className="flex gap-2 px-4">
        {items.map((item) => {
          const Icon = item.icon
          const active =
            location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-all',
                active
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-border bg-background text-foreground hover:border-border hover:bg-muted',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {t(item.labelKey)}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function SettingsLayout() {
  // Desktop: the navy SettingsSidebar (rendered by AppLayout) replaces this
  // page's previous in-content aside — we don't double-render the nav.
  // Mobile: the horizontal pill tabs remain since the navy sidebar lives
  // behind the burger menu (one tap away, but pills are faster to skim).
  return (
    <div className="flex flex-col gap-6">
      <SettingsMobileTabs />
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  )
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: 'settings-layout',
  component: SettingsLayout,
})
